<template>
  <v-card class="import-schedule">
    <v-card-title>
      {{ $t('whitelist.import_title') }}
    </v-card-title>
    <v-card-text class="d-flex flex-column pa-0" style="height: 70vh;">
      <v-stepper v-model="step"
                 class="flex-grow-1 d-flex flex-column"
                 elevation="0">
        <v-stepper-header>
          <v-stepper-step v-for="idx in [1,2,3]"
                          :complete="step > idx"
                          :step="idx">
            {{ $t(`whitelist.import_step_${idx}`) }}
          </v-stepper-step>
        </v-stepper-header>
        <v-stepper-items class="flex-grow-1">
          <v-stepper-content step="1">
            <div class="step-1-wrapper">
              <v-btn color="primary"
                     x-large
                     @click="downloadTemplate">
                {{ $t('whitelist.download_template')}}
              </v-btn>
              <v-btn color="primary"
                     x-large
                     @click="$refs.excelInput.click()">
                {{ $t('whitelist.upload_template')}}
              </v-btn>
              <input type="file"
                     accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                     hidden
                     ref="excelInput"
                     @change="onExcelInputChange">
            </div>
          </v-stepper-content>
          <v-stepper-content step="2">
            <v-tabs grow v-model="tabIndex"
                    class="step-2-wrapper">
              <v-tab :disabled="errors.length === 0">
                {{ $t('whitelist.import_errors') }}
                {{ errors ? `(${errors.length})` : '' }}
              </v-tab>
              <v-tab-item>
                <v-data-table :items="errors"
                              :headers="errorHeaders"
                              class="import-schedule--table"
                              fixed-header>
                </v-data-table>
              </v-tab-item>

              <v-tab :disabled="added.length === 0">
                {{ $t('whitelist.import_added') }}
                {{ added ? `(${added.length})` : '' }}
              </v-tab>
              <v-tab-item>
                <v-text-field outlined
                              hide-details
                              prepend-icon="fas fa-search"
                              label="Search"
                              class="mt-4 flex-grow-0"
                              v-model="addedSearch"></v-text-field>
                <v-data-table :items="added"
                              :headers="whitelistHeaders"
                              class="import-schedule--table"
                              :search="addedSearch"
                              :items-per-page="-1"
                              hide-default-footer
                              multi-sort
                              fixed-header>
                </v-data-table>
              </v-tab-item>

              <v-tab :disabled="removed.length === 0">
                {{ $t('whitelist.import_removed') }}
                {{ removed ? `(${removed.length})` : '' }}
              </v-tab>
              <v-tab-item>
                <v-text-field outlined
                              hide-details
                              prepend-icon="fas fa-search"
                              label="Search"
                              class="mt-4 flex-grow-0"
                              v-model="removedSearch"></v-text-field>
                <v-data-table :items="removed"
                              :headers="whitelistHeaders"
                              class="import-schedule--table"
                              :search="removedSearch"
                              :items-per-page="-1"
                              hide-default-footer
                              multi-sort
                              fixed-header>
                </v-data-table>
              </v-tab-item>
            </v-tabs>
          </v-stepper-content>
          <v-stepper-content step="3">
            <div class="step-1-wrapper">
              {{ $t('whitelist.import_success_msg') }}
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card-text>
    <v-card-actions>
      <v-btn color="secondary"
             @click="$emit('submit', false)"
             depressed
             large>
        {{ $t('close') }}
      </v-btn>
      <v-btn color="secondary"
             @click="step -= 1"
             depressed
             large
             v-if="step === 2">
        {{ $t('back') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary"
             @click="confirm"
             large
             v-if="step === 2 && excelFile">
        {{ $t('confirm') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {ref} from "@vue/composition-api";
import {ACTION_TYPES} from "@/store/types";

export default {
  name: 'ImportAdfsWhiteListDialog',
  setup(props, {root, emit}) {
    const step = ref(1);

    // Step 1
    const downloadTemplate = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: 'admin/whitelist/export/',
        params: {},
        opt: {extra: {responseType: 'arraybuffer'}},
      });

      let blob = new Blob([response.data], {type: response.headers.get('content-type')});
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'whitelists.xlsx'
      link.click();
    };
    const excelInput = ref();
    const excelFile = ref(null);
    const onExcelInputChange = async function (e) {
      excelFile.value = null;
      errors.value = [];
      added.value = [];
      removed.value = [];

      const file = e.target.files[0];
      excelInput.value.value = '';
      if (!file) return

      let formData = new FormData();
      formData.append('file', file, file.name);

      let response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: 'admin/whitelist/import/?dry_run=1',
        params: formData,
        opt: {
          throw_error: true,
        }
      });

      if (response.body.success) {
        if (response.body.errors) {
          errors.value = response.body.errors.map((e) => {
            return {error: e};
          });
        } else {
          errors.value = [];
        }

        if (response.body.added) {
          added.value = response.body.added.map((a) => {
            return {username: a};
          });
        } else {
          added.value = [];
        }

        if (response.body.removed) {
          removed.value = response.body.removed.map((a) => {
            return {username: a};
          });
        } else {
          removed.value = [];
        }

        step.value = 2;

        if (errors.value.length === 0) {
          if (added.value.length > 0) {
            tabIndex.value = 1;
          } else {
            tabIndex.value = 2;
          }
          excelFile.value = file;
        } else {
          tabIndex.value = 0;
        }
      }
    };

    // Step 2
    const tabIndex = ref(0);
    const errors = ref([]);
    const errorHeaders = ref([{text: '', value: 'error'}]);
    const added = ref([]);
    const removed = ref([]);

    const whitelistHeaders = ref([
      {text: root.$t('whitelist.username'), value: 'username'},
    ])

    const confirm = async function () {
      let formData = new FormData();
      formData.append('file', excelFile.value, excelFile.value.name);

      let response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: 'admin/whitelist/import/?dry_run=0',
        params: formData,
        opt: {
          throw_error: true,
        }
      });

      if (response.body.success) {
        excelFile.value = null;
        step.value = 3;
      }
    }

    return {
      step,

      downloadTemplate,
      excelInput,
      excelFile,
      onExcelInputChange,

      tabIndex,
      errors,
      errorHeaders,
      added,
      addedSearch: ref(''),
      removed,
      removedSearch: ref(''),
      whitelistHeaders,

      confirm,
    }
  },
}
</script>

<style lang="less">
.import-schedule {
  .v-stepper__items {
    flex: 1 1 0;
    display: flex;

    .v-stepper__content {
      display: flex;

      .v-stepper__wrapper {
        flex: 1 0 0;
        display: flex;

        .step-1-wrapper {
          flex: 1 0 0;
          display: flex;
          justify-content: space-evenly;
          align-items: center;
        }

        .step-2-wrapper {
          display: flex;
          flex-direction: column;

          .v-tabs-bar {
            flex: 0 0 auto;
          }

          .v-tabs-items {
            flex: 1 1 0;

            .v-window__container {
              height: 100% !important;

              .v-window-item {
                height: 100% !important;
                display: flex;
                flex-direction: column;
              }
            }
          }
        }

        .import-schedule--table {
          flex: 1 1 0;
          display: flex;
          flex-direction: column;

          .v-data-table__wrapper {
            flex: 1 1 0;
          }

          .v-data-footer {
            margin-right: 0 !important;
          }
        }

      }
    }
  }
}
</style>
